import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useSEO } from "@/hooks/useSEO";
import { getLocalizedRegionSEO } from "@/lib/seoUtils";
import { Plus, Filter, SortAsc, MapPin, Lock, Heart, Bookmark, Map, Search, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { RouteCard } from "@/components/RouteCard";
import { LoginModal } from "@/components/LoginModal";
import { LimitBanner } from "@/components/LimitBanner";
import { UpgradeModal } from "@/components/UpgradeModal";
import { useAuth } from "@/lib/authContext";
import { useQuery } from "@tanstack/react-query";
import { PROVINCES, FREE_TIER_LIMITS } from "@shared/schema";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useI18n } from "@/lib/i18n";
import { getPreferredGeo } from "@/lib/geoPreferences";
import FeatureGuard from "@/components/FeatureGuard";
import OfflineCache from "@/lib/offline-cache";


type SortOption = "popular" | "new" | "saves" | "shops" | "distance";

export default function RoutesPage({ initialFilter }: { initialFilter?: 'favorites' | 'wishlist' } & any) {
  const [, setLocation] = useLocation();
  const { isLoggedIn, tier, remainingRoutes, user } = useAuth();
  const { locale, t } = useI18n();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [search, setSearch] = useState(""); // Active search term for filtering
  const [searchInput, setSearchInput] = useState(""); // Input field value

  const [country, setCountry] = useState<string>(() => getPreferredGeo(user, locale).country);
  const [province, setProvince] = useState<string>("all");
  const [city, setCity] = useState<string>("all");
  const [sort, setSort] = useState<string>("popular");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const pageTitle = initialFilter === 'favorites' ? t("routes.page.favorites") :
    initialFilter === 'wishlist' ? t("routes.page.wishlist") :
      t("routes.page.title");
  const pageDescription = initialFilter ? '' : t("routes.page.description");

  // SEO Logic
  const regionSEO = getLocalizedRegionSEO({
    regionName: province !== 'all' ? province : country,
    countryCode: country,
    stateOrProvince: province !== 'all' ? province : undefined
  }, locale);

  useSEO({
    titleKey: "seo.home.title", // Routes index uses the same "Plan geoptimaliseerde routes" theme
    descriptionKey: "seo.home.description",
    dynamicValues: { region: regionSEO },
    isRawTitle: true
  });


  // Data will be managed by react-query's staleTime/cacheTime
  // No need to invalidate on every mount unless explicitly requested by user action

  const { data: routes = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/routes/public", { country, sort, initialFilter }],
    queryFn: async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      const params = new URLSearchParams();
      // Personal favorites/wishlist should not be hidden by default NL filter.
      params.set("country", initialFilter ? "all" : country);
      params.set("sort", sort);

      if (initialFilter === 'favorites') params.set('favorites', 'true');
      if (initialFilter === 'wishlist') params.set('wishlist', 'true');

      const headers: Record<string, string> = {};
      if (token && initialFilter) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`/api/routes/public?${params.toString()}`, { headers });
      if (!res.ok) throw new Error("Failed to fetch routes");

      const data = await res.json();

      // CACHE LOGIC: Save routes to OfflineCache (72h)
      // Only cache generic list to prevent storage bloat
      if (!initialFilter && !search && country !== 'all') {
        OfflineCache.set('routes-public-list', data, 72);
      }

      return data;
    },
    placeholderData: (prev) => {
      // Fallback to cache (must return undefined if cache is empty/null to trigger default [])
      if (prev) return prev;
      if (!initialFilter && !search) {
        const cached = OfflineCache.get('routes-public-list');
        return cached || undefined;
      }
      return undefined;
    }
  });

  const safeRoutes = Array.isArray(routes) ? routes : [];

  useEffect(() => {
    if (initialFilter) return;
    if (isLoading) return;
    if (country === "all") return;
    if ((routes || []).length > 0) return;
    // Fallback for countries with no route data yet.
    setCountry("all");
  }, [country, initialFilter, isLoading, routes]);

  const { data: myRoutes = [] } = useQuery({
    queryKey: ['my-routes'],
    queryFn: async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) return [];
      const res = await fetch("/api/routes", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: isLoggedIn
  });

  const { data: dashboardData } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const res = await fetch(`/api/users/${user.id}/dashboard`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: isLoggedIn
  });

  const favoriteRoutes = dashboardData?.favoriteRoutes || [];
  const wishlistRoutes = dashboardData?.wishlistRoutes || [];

  const cities = Array.from(new Set(
    safeRoutes
      .filter((r: any) => province === "all" || r.allProvinces?.includes(province) || r.region === province)
      .flatMap((r: any) => r.allCities || (r.city ? [r.city] : []))
  )).sort();

  const filteredRoutes = safeRoutes
    .filter((route: any) => {
      if (search) {
        const term = search.toLowerCase();
        const matchesName = route.name.toLowerCase().includes(term);
        const matchesCity = route.allCities?.some((c: string) => c.toLowerCase().includes(term)) || route.city?.toLowerCase().includes(term);
        const matchesShop = route.allShopNames?.some((s: string) => s.toLowerCase().includes(term));

        if (!matchesName && !matchesCity && !matchesShop) {
          return false;
        }
      }

      if (province !== "all") {
        const matchesProvince = route.allProvinces?.includes(province) || route.region === province;
        if (!matchesProvince) return false;
      }

      if (city !== "all") {
        const matchesCity = route.allCities?.includes(city) || route.city === city;
        if (!matchesCity) return false;
      }

      return true;
    });

  const handleCreateRoute = () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
    }
  };

  const handleSaveRoute = () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
    }
  };

  const handleDeleteRoute = async (routeId: string) => {
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) return;

      const res = await fetch(`/api/routes/${routeId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error("Delete failed");

      toast({
        title: "Route removed",
        description: "The route was removed from your overview.",
      });

      // Invalidate both public and private routes
      queryClient.invalidateQueries({ queryKey: ["/api/routes/public"] });
      queryClient.invalidateQueries({ queryKey: ["my-routes"] });
    } catch (error) {
      toast({
        title: "Delete failed",
        description: "An error occurred while deleting the route.",
        variant: "destructive",
      });
    }
  };

  const handleSearchSubmit = () => {
    if (searchInput.trim()) {
      // Navigate to new page with search params
      const params = new URLSearchParams();
      params.set('search', searchInput);
      if (country !== "all") params.set('country', country);
      if (province !== "all") params.set('province', province);
      if (city !== "all") params.set('city', city);
      if (sort) params.set('sort', sort);
      setLocation(`/routes/search?${params.toString()}`);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <FeatureGuard feature="routes" cacheKey="routes-public-list" ttlHours={72}>
        <main className="flex-1 container mx-auto px-4 py-8 space-y-12">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">{pageTitle}</h1>
              {pageDescription && (
                <p className="text-muted-foreground mt-1">
                  {pageDescription.replace('{routes.length}', String(safeRoutes.length))}
                </p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
              <div className="flex items-center bg-background border rounded-lg overflow-hidden focus-within:ring-2 ring-primary/20 transition-all">
                <Input
                  placeholder={t("routeList.searchPlaceholder")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                  className="border-none focus-visible:ring-0 shadow-none h-10 w-full sm:w-[200px]"
                />
              </div>
              <Button
                variant="outline"
                className="gap-2 h-10 order-2 sm:order-none"
                onClick={handleSearchSubmit}
              >
                <Search className="h-4 w-4" />
                {t("common.search")}
              </Button>
              <Link href={isLoggedIn ? "/routes/new" : "#"} className="w-full sm:w-auto order-3 sm:order-none">
                <Button
                  className="w-full sm:w-auto gap-2 h-10"
                  onClick={() => !isLoggedIn && setShowLoginModal(true)}
                >
                  <Plus className="h-4 w-4" />
                  {t("routes.new")}
                </Button>
              </Link>
            </div>
          </div>

          {
            tier === "free" && !initialFilter && (
              <LimitBanner
                current={FREE_TIER_LIMITS.maxRoutes - remainingRoutes}
                max={FREE_TIER_LIMITS.maxRoutes}
                type="routes"
                onUpgrade={() => setShowUpgradeModal(true)}
              />
            )
          }

          {/* Dashboard Sections (Only if no initialFilter is set) */}
          {
            !initialFilter ? (
              <>
                {/* 1. My Routes (Max 6) */}
                {isLoggedIn && myRoutes.length > 0 && (
                  <section>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-semibold flex items-center gap-2">
                        <Lock className="h-5 w-5 text-primary" />
                        {t("routes.mine")}
                      </h2>
                      {myRoutes.length > 6 && (
                        <Link href="/routes/my-routes">
                          <Button variant="ghost" className="text-primary hover:text-primary/80">
                            {t("routes.more", { count: String(myRoutes.length - 6) })} <ArrowRight className="ml-1 h-4 w-4" />
                          </Button>
                        </Link>
                      )}
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {myRoutes.slice(0, 6).map((route: any) => (
                        <RouteCard
                          key={route.id}
                          route={route}
                          onSave={handleSaveRoute}
                          onDelete={handleDeleteRoute}
                        />
                      ))}
                    </div>
                  </section>
                )}

                {/* 2 & 3. Favorites & Bucketlist (Max 2 each, side-by-side on desktop) */}
                {isLoggedIn && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Favorites */}
                    <section>
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold flex items-center gap-2">
                          <Heart className="h-5 w-5 text-primary" />
                          {t("routes.favorites.title")}
                        </h2>
                        <Link href="/routes/favorites">
                          <Button variant="ghost" className="text-primary hover:text-primary/80">
                            {t("routes.viewAll")} <ArrowRight className="ml-1 h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                      {favoriteRoutes.length > 0 ? (
                        <div className="grid grid-cols-2 gap-4">
                          {favoriteRoutes.slice(0, 2).map((route: any) => (
                            <RouteCard key={`fav-${route.id}`} route={route} />
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground italic bg-muted/20 p-4 rounded text-center">
                          {t("routes.empty.favorites")}
                        </div>
                      )}
                    </section>

                    {/* Bucketlist */}
                    <section>
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold flex items-center gap-2">
                          <Bookmark className="h-5 w-5 text-primary" />
                          {t("routes.wishlist.title")}
                        </h2>
                        <Link href="/routes/wishlist">
                          <Button variant="ghost" className="text-primary hover:text-primary/80">
                            {t("routes.viewAll")} <ArrowRight className="ml-1 h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                      {wishlistRoutes.length > 0 ? (
                        <div className="grid grid-cols-2 gap-4">
                          {wishlistRoutes.slice(0, 2).map((route: any) => (
                            <RouteCard key={`wish-${route.id}`} route={route} />
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground italic bg-muted/20 p-4 rounded text-center">
                          {t("routes.empty.wishlist")}
                        </div>
                      )}
                    </section>
                  </div>
                )}

                {/* 4. Community Routes (Max 6) */}
                <section>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold flex items-center gap-2">
                      <Map className="h-5 w-5 text-primary" />
                      {t("routes.community.title")}
                    </h2>
                    <Link href="/routes/browse">
                      <Button variant="ghost" className="text-primary hover:text-primary/80">
                        {t("routes.viewAll")} <ArrowRight className="ml-1 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>

                  {/* Optional: Simple filters for dashboard if needed, or just link to browse */}

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {isLoading ? (
                      [1, 2, 3].map((i) => (
                        <div key={i} className="h-[300px] bg-muted/20 animate-pulse rounded-lg" />
                      ))
                    ) : (
                      safeRoutes.slice(0, 6).map((route: any) => (
                        <RouteCard
                          key={route.id}
                          route={route}
                          onSave={handleSaveRoute}
                        />
                      ))
                    )}
                  </div>
                </section>
              </>
            ) : (
              /* Filtered View (Favorites/Wishlist Pages) */
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {isLoading ? (
                  [1, 2, 3].map((i) => (
                    <div key={i} className="h-[300px] bg-muted/20 animate-pulse rounded-lg" />
                  ))
                ) : (
                  filteredRoutes.map((route: any) => (
                    <RouteCard
                      key={route.id}
                      route={route}
                      onSave={handleSaveRoute}
                    />
                  ))
                )}
                {filteredRoutes.length === 0 && !isLoading && (
                  <div className="col-span-full text-center py-12 text-muted-foreground">
                    {t("routes.empty.noneIn", { name: pageTitle })}
                    <div className="mt-4">
                      <Link href="/routes/browse">
                        <Button variant="outline">{t("routes.discover")}</Button>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )
          }
        </main >
      </FeatureGuard >


      <LoginModal
        open={showLoginModal}
        onOpenChange={setShowLoginModal}
        action="route"
      />

      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        variant="routes"
      />
    </div >
  );
}


